import { useMemo, useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  SingleSelect,
  SingleSelectOption,
} from 'src/components/common/SingleSelect';
import {
  WAREHOUSE_TYPE,
  WAREHOUSE_TYPE_VALUES,
  isWarehouseType,
  type WarehouseType,
} from '@erp/shared-types';
import { useUpdateWarehouse } from 'src/api/rpc/hooks/inventory-management/useUpdateWarehouse';
import type { WarehouseDTO } from 'src/api/rpc/hooks/inventory-management/useWarehouses';
import useDivisions from 'src/api/organization/useDivisions';

interface EditWarehouseModalProps {
  warehouse: WarehouseDTO;
  onClose: (refetch?: boolean) => void;
}

const TYPE_LABELS: Record<WarehouseType, string> = {
  [WAREHOUSE_TYPE.HUB]: 'Hub',
  [WAREHOUSE_TYPE.BRANCH]: 'Branch',
};

export function EditWarehouseModal({
  warehouse,
  onClose,
}: EditWarehouseModalProps) {
  const gt = useGT();
  const { mutate: updateWarehouse, isPending } = useUpdateWarehouse(() =>
    onClose(true),
  );
  const { data: divisionsData } = useDivisions(true);

  const [formData, setFormData] = useState({
    name: warehouse.name,
    description: warehouse.description ?? '',
    type: warehouse.type,
    divisionId: warehouse.division?.id ?? null,
  });

  const typeOptions: SingleSelectOption[] = useMemo(
    () =>
      WAREHOUSE_TYPE_VALUES.map((value) => ({
        id: value,
        label: TYPE_LABELS[value],
      })),
    [],
  );

  const divisionOptions: SingleSelectOption[] = useMemo(
    () =>
      (divisionsData?.divisions ?? []).map((d) => ({
        id: d.id,
        label: d.name,
      })),
    [divisionsData],
  );

  const selectedType = useMemo(
    () => typeOptions.find((o) => o.id === formData.type) ?? null,
    [typeOptions, formData.type],
  );

  const selectedDivision = useMemo(
    () => divisionOptions.find((o) => o.id === formData.divisionId) ?? null,
    [divisionOptions, formData.divisionId],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateWarehouse({
      id: warehouse.id,
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      type: formData.type,
      divisionId: formData.divisionId,
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            <T context="modal title for editing a warehouse">Edit Warehouse</T>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-warehouse-name">
                <T context="required field label for warehouse name in edit modal">
                  Name *
                </T>
              </Label>
              <Input
                id="edit-warehouse-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-warehouse-description">
                <T context="optional field label for warehouse description in edit modal">
                  Description
                </T>
              </Label>
              <Input
                id="edit-warehouse-description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <SingleSelect
                label={gt('Type', {
                  context: 'warehouse type select label (hub or branch)',
                })}
                options={typeOptions}
                value={selectedType}
                onChange={(opt) =>
                  setFormData((prev) => ({
                    ...prev,
                    type: opt && isWarehouseType(opt.id) ? opt.id : null,
                  }))
                }
                placeholder={gt('Unspecified', {
                  context: 'placeholder when warehouse type is unset',
                })}
                fullWidth
                allowClear
              />
              <SingleSelect
                label={gt('Division', {
                  context:
                    'warehouse division select label (the division this warehouse belongs to)',
                })}
                options={divisionOptions}
                value={selectedDivision}
                onChange={(opt) =>
                  setFormData((prev) => ({
                    ...prev,
                    divisionId: opt?.id ?? null,
                  }))
                }
                placeholder={gt('Unspecified', {
                  context: 'placeholder when warehouse division is unset',
                })}
                fullWidth
                allowClear
              />
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose()}
              disabled={isPending}
            >
              <T context="button to dismiss edit warehouse modal without saving">
                Cancel
              </T>
            </Button>
            <Button type="submit" disabled={isPending || !formData.name.trim()}>
              {isPending ? (
                <T context="submit button label while warehouse is being saved">
                  Saving...
                </T>
              ) : (
                <T context="submit button label when saving warehouse edits">
                  Save Changes
                </T>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
